import { useParams, Link } from 'react-router-dom'
import * as actions from '@actions'
import { modalManageLLMPromptOpen } from '$store'
import useCollectionViewModel, {
    TAB_SEARCH_DOCS_KEY,
    TAB_SEARCH_INDEX_KEY,
    TAB_BROWSE_KEY,
    TAB_ES_SEARCH_WILDCARD,
} from './useCollectionViewModel'
import cn from 'clsx'

import Title from '@components/shared/Title'
import { SelectRunsheetDialog } from '@components/collection/SelectRunsheetDialog'
import { UpsertRunsheet } from '@components/tract/UpsertRunsheet'
import ConfirmationModal from '@components/shared/ConfirmationModal'
import EditDocument from '@components/collection/EditDocument'
import { DocumentViewerModal } from '@components/collection/DocumentViewerV2'

import 'flatpickr/dist/themes/dark.css'
import './Collection.css'
import { formatDate } from '@/utils/date'
import 'react-mosaic-component/react-mosaic-component.css'
import CollectionBrowseView from '@components/collection/Collection/views/CollectionBrowseView'
import CollectionWildcardSearchView from '@components/collection/Collection/views/CollectionWildcardSearchView'
import CollectionDocumentSearchView from '@components/collection/Collection/views/CollectionDocumentSearchView'
import { MdCollectionsBookmark, MdBusiness, MdSettings } from 'react-icons/md'
import { toast } from 'react-toastify'
import ManageLLMPromptModal from '$components/Modals/ManageLLMPromptModal'
import { useWireState } from '@forminator/react-wire'
import { ROLE_PARSE_ADMIN } from '@/constants'
import useUserHasRoles from '@/hook/useUserHasRoles'
import SelectRunsheetTabs from '@/components/tract/Runsheet/SelectRunsheetTabs'

export const CollectionComponent = () => {
    
    const { collectionId, routeActiveTab } = useParams()
    
    const vm = useCollectionViewModel({ collectionId, routeActiveTab })
    
    const [, setOpen] = useWireState(modalManageLLMPromptOpen)
    const isParseAdmin = useUserHasRoles([ROLE_PARSE_ADMIN])
    
    return (
        
        <div className="CollectionDetail">
            
            <Title>{vm?.currentCollection?.name ?? 'Collection'}</Title>
            
            <div id="tabs">
                <header className="flex flex-col md:flex-row mb-4">
                    {/* <div className="flex gap-3 mb-3 nav nav-tabs px-2">
                        {vm.tabs.map(({ key, title }) => (
                            <Link
                                key={key}
                                className="nav-item"
                                to={`/collection/${collectionId}/${key}`}>
                                <span className={cn('nav-link pt-0 pb-2', { active: key === vm.activeTab })}>
                                    {title}
                                </span>
                            </Link>
                        ))}
                    </div> */}
                    <div className="join">
                        {vm.tabs.map(({ key, title }) => (
                            <Link
                                key={key}
                                className={cn('join-item btn', { 'bg-primary': key === vm.activeTab })}
                                to={`/collection/${collectionId}/${key}`}
                                data-test-id={`CollectionViewTab${key}`}>
                                {title}
                            </Link>
                        ))}
                    </div>
                    <div className="flex items-center gap-2 ml-3 order-1 md:order-none mb-3 md:mb-0">
                        <button
                            className="btn btn-primary btn-sm"
                            disabled={!vm.markedDocuments.length || !vm.selectedRunsheet?.id || vm.isAddingToRunsheet}
                            onClick={() => vm.selectedRunsheet.tabs.length >= 1 
                                ? vm.showHideSelectRunsheetTabs(!vm.showSelectRunsheetTabs) 
                                : vm.onAddToRunsheetClick()}>
                            {vm.isAddingToRunsheet && <span className="loading loading-spinner loading-xs" />}
                            Add to Runsheet
                        </button>
                        <button
                            className="btn btn-primary btn-sm"
                            onClick={() => vm.setSelectRunsheetDialogOpen(true)}>
                            Open Runsheet
                        </button>
                    </div>
                    <div className="flex flex-col mb-3 md:mb-0 pr-2">
                        <div className="flex justify-end items-center gap-2">
                            <span>{vm?.currentCollection?.name ?? ''}</span>
                            {!isParseAdmin && <MdCollectionsBookmark className="text-xl" />}
                            {isParseAdmin && !vm?.currentCollection?.isParse &&
                            <div
                                className="tooltip tooltip-bottom-left"
                                data-tip="LLM Prompt Configuration">
                                <MdSettings
                                    className="text-xl cursor-pointer"
                                    onClick={() => setOpen(true)} />
                            </div>}
                            {vm.currentCollection?.isParse && (
                                <MdBusiness
                                    className="text-xl tooltip tooltip-primary tooltip-bottom"
                                    data-tip="This is an official county collection" />
                            )}
                        </div>
                        {vm?.currentCollection?.isParse && vm?.currentCollection?.minCertDate 
                        && vm?.currentCollection?.maxCertDate && (
                            <div
                                className="text-muted text-right text-sm tooltip tooltip-primary"
                                data-tip="Certification Date">
                                {formatDate(vm?.currentCollection?.minCertDate, 'MM/DD/YYYY')}
                                {' '} to {' '}
                                {formatDate(vm?.currentCollection?.maxCertDate, 'MM/DD/YYYY')}
                            </div>
                        )}
                    </div>
                </header>
                
                {/* Sidebar x Main Content horizontal split */}
                {(vm.activeTab === TAB_SEARCH_DOCS_KEY || vm.activeTab === TAB_SEARCH_INDEX_KEY) && (
                    <CollectionDocumentSearchView vm={vm} />
                )}
                
                {vm.activeTab === TAB_BROWSE_KEY && (
                    <CollectionBrowseView vm={vm} collectionId={collectionId} />
                )}
                
                {vm.activeTab === TAB_ES_SEARCH_WILDCARD && (
                    <CollectionWildcardSearchView vm={vm} />
                )}
                
                {/* {vm.activeTab === TAB_ES_SEARCH_ADVANCED && (
                    <div className="">@todo advanced</div>
                )} */}
            
            </div>
            
            <SelectRunsheetDialog
                runsheets={vm.allRunsheets}
                open={vm.selectRunsheetDialogOpen}
                setOpen={vm.setSelectRunsheetDialogOpen}
                setCreateRunsheetDialogOpen={vm.setCreateRunsheetDialogOpen}
                onSelectRunsheet={selectedRunsheet => {
                    if (selectedRunsheet?.id)
                        vm.setSearchParams(prev => {
                            prev.set('openRunsheetId', selectedRunsheet.id)
                            return prev
                        })
                }}/>
            
            <UpsertRunsheet
                showAdd={vm.createRunsheetDialogOpen}
                origin="document"
                showHideAdd={vm.setCreateRunsheetDialogOpen}
                redirect={false}
                runsheetSaved={runsheet => {
                    if (runsheet) {
                        vm.allRunsheets.unshift(runsheet)
                        vm.setSelectedRunsheet(runsheet)
                        vm.setRunsheetDocs(runsheet.rdj)
                    }
                    
                    vm.setCreateRunsheetDialogOpen(false)
                }} />
            
            <ConfirmationModal
                showModal={vm.showDeleteDocument}
                showHideModal={vm.setShowDeleteDocument}
                title="Delete Document"
                message={` Are you sure you want to delete the document with name ${vm.documentName}`}
                onConfirm={async () => {
                    try {
                        await actions.deleteDocuments([vm.documentInfo.id])
                        toast.success('Document deleted successfully.')
                    } catch (e) {
                        // noop - treated within action
                    }
                }}
                data-test-id="DeleteDocumentConfirmationModal" />
            
            <EditDocument
                id="create-collection"
                showEditDocument={vm.showEditDocument}
                setShowEditDocument={vm.setShowEditDocument}
                documentInfo={vm.documentInfo} />
            
            <SelectRunsheetTabs 
                showModal={vm.showSelectRunsheetTabs}
                showHideModal={vm.showHideSelectRunsheetTabs}
                onAddToRunsheetClick={vm.onAddToRunsheetClick}/>
            
            <ManageLLMPromptModal
                id={collectionId}
                routeBase="promptConfig" />
            
            <DocumentViewerModal />
        
        </div>
        
    )
    
}
